import { useEffect, useRef } from "react";

export interface StickerTransform {
    x: number;
    y: number;
    scale: number;
    rotation: number; // radians
}

interface ActivePointer {
    clientX: number;
    clientY: number;
}

interface GestureBaseline {
    pointers: { id: number; clientX: number; clientY: number }[];
    transform: StickerTransform;
    // For 2-finger gestures, baseline of the gesture itself.
    distance: number;
    angle: number;
    midpoint: { x: number; y: number };
}

// Multi-touch gestures on a canvas element. The currently-selected sticker is
// translated, scaled, and rotated as the user drags one or two fingers anywhere
// inside the canvas. Single-finger drag = translation only. Two-finger gesture
// = simultaneous translate + scale + rotate.
//
// We hand-roll this rather than use Framer Motion because Framer's drag is
// single-pointer; pinch+rotate need explicit multi-touch math.
export function useStickerGestures(opts: {
    canvasRef: React.RefObject<HTMLElement>;
    enabled: boolean;
    // Flips true once the canvas element is actually mounted + measured. Needed
    // because IonModal mounts its content lazily, so canvasRef.current is null
    // when `enabled` first flips true — without this the listeners would never
    // attach. Re-running on `ready` re-attaches once the canvas exists.
    ready?: boolean;
    getTransform: () => StickerTransform | null;
    onChange: (next: StickerTransform) => void;
    // Fire when a manipulation of the selected sticker begins / ends (all
    // pointers released). The editor uses these to show a drag-to-delete trash
    // zone and to test for a drop over it.
    onGestureStart?: () => void;
    onGestureEnd?: () => void;
}) {
    const { canvasRef, enabled, ready = true, getTransform, onChange, onGestureStart, onGestureEnd } = opts;
    const activePointers = useRef<Map<number, ActivePointer>>(new Map());
    const baseline = useRef<GestureBaseline | null>(null);

    useEffect(() => {
        const el = canvasRef.current;
        if (!el || !enabled || !ready) return;

        const captureBaseline = () => {
            const t = getTransform();
            if (!t) {
                baseline.current = null;
                return;
            }
            const pointers = Array.from(activePointers.current.entries()).map(([id, p]) => ({
                id,
                clientX: p.clientX,
                clientY: p.clientY,
            }));
            if (pointers.length === 0) {
                baseline.current = null;
                return;
            }
            if (pointers.length === 1) {
                baseline.current = {
                    pointers,
                    transform: { ...t },
                    distance: 0,
                    angle: 0,
                    midpoint: { x: pointers[0].clientX, y: pointers[0].clientY },
                };
                return;
            }
            const [a, b] = pointers;
            const dx = b.clientX - a.clientX;
            const dy = b.clientY - a.clientY;
            baseline.current = {
                pointers,
                transform: { ...t },
                distance: Math.hypot(dx, dy) || 1,
                angle: Math.atan2(dy, dx),
                midpoint: { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 },
            };
        };

        const onPointerDown = (e: PointerEvent) => {
            // Only react to pointers inside the canvas surface.
            activePointers.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
            try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
            // If a gesture is already running (e.g. a 2nd finger lands), re-baseline
            // to include the new pointer. Otherwise DEFER the baseline to the first
            // move — by then the sticker's own onPointerDown selection has flushed,
            // so we manipulate the sticker the user actually touched.
            if (baseline.current) captureBaseline();
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!activePointers.current.has(e.pointerId)) return;
            activePointers.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
            // Lazily capture the baseline on the first move (selection has settled).
            if (!baseline.current) {
                captureBaseline();
                if (baseline.current) onGestureStart?.();
            }
            const b = baseline.current;
            if (!b) return;

            const pointers = Array.from(activePointers.current.values());
            if (pointers.length === 1) {
                const dx = pointers[0].clientX - b.midpoint.x;
                const dy = pointers[0].clientY - b.midpoint.y;
                onChange({
                    x: b.transform.x + dx,
                    y: b.transform.y + dy,
                    scale: b.transform.scale,
                    rotation: b.transform.rotation,
                });
            } else if (pointers.length >= 2) {
                const [p1, p2] = pointers;
                const dx = p2.clientX - p1.clientX;
                const dy = p2.clientY - p1.clientY;
                const dist = Math.hypot(dx, dy) || 1;
                const ang = Math.atan2(dy, dx);
                const mid = { x: (p1.clientX + p2.clientX) / 2, y: (p1.clientY + p2.clientY) / 2 };
                const scaleFactor = dist / b.distance;
                const rotationDelta = ang - b.angle;
                const midDx = mid.x - b.midpoint.x;
                const midDy = mid.y - b.midpoint.y;
                onChange({
                    x: b.transform.x + midDx,
                    y: b.transform.y + midDy,
                    scale: clamp(b.transform.scale * scaleFactor, 0.25, 6),
                    rotation: b.transform.rotation + rotationDelta,
                });
            }
        };

        const releasePointer = (e: PointerEvent) => {
            if (!activePointers.current.has(e.pointerId)) return;
            activePointers.current.delete(e.pointerId);
            try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
            // Re-baseline so the next move continues smoothly with the remaining pointer(s).
            captureBaseline();
            // Last finger up → the manipulation ends (commit / drop test).
            if (activePointers.current.size === 0) {
                onGestureEnd?.();
            }
        };

        el.addEventListener("pointerdown", onPointerDown);
        el.addEventListener("pointermove", onPointerMove);
        el.addEventListener("pointerup", releasePointer);
        el.addEventListener("pointercancel", releasePointer);
        el.addEventListener("pointerleave", releasePointer);

        return () => {
            el.removeEventListener("pointerdown", onPointerDown);
            el.removeEventListener("pointermove", onPointerMove);
            el.removeEventListener("pointerup", releasePointer);
            el.removeEventListener("pointercancel", releasePointer);
            el.removeEventListener("pointerleave", releasePointer);
            activePointers.current.clear();
            baseline.current = null;
        };
    }, [canvasRef, enabled, ready, getTransform, onChange, onGestureStart, onGestureEnd]);
}

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
}
